import { useState, useMemo, useEffect, useCallback } from 'react';

import { LocationData } from '../LocationsContainer/types';
import styles from '../styles.module.css';
import PowerIcon from '@/assets/icons/power-site.svg?react';
import UnlinkIcon from '@/assets/icons/unlink.svg?react';
import {
  AdminCardBody,
  AdminCardHeader,
  AdminTableBody
} from '@/components/AdminToolComponents/card';
import ContainerLoader from '@/components/AdminToolComponents/containerLoader';
import StatusIndicator from '@/components/AdminToolComponents/statusIndicator';
import DataTable, { DataTableColumn } from '@/components/DataTable';
import { Button } from '@/components/Form/Button';
import Loader from '@/components/Form/Loader';
import PlusButton from '@/components/Form/PlusButton';
import SearchComponent from '@/components/LocationDetails/searchComponent';
import useModal from '@/context/ModalContext/useModal';
import useGetLocationsList from '@/hooks/services/Locations';

interface LocationsAssignedTableProps {
  locationsForTable: LocationData[];
  canAssignUnassignLocations?: boolean;
  onAssignLocations?: OnAssignLocations;
  onUnassignLocation?: OnUnassignLocation;
  onRowClick?: (row: LocationData) => void;
  loading?: boolean;
}

export const getLocationsRows = (locations: any[]): LocationData[] =>
  (locations || []).map((location: any) => ({
    id: location.id,
    type: location.type || 'power',
    name: location.name,
    category: location.category || 'powersite',
    status: (location.status as 'Active' | 'Inactive') || 'Active',
    serialNumber: location.id,
    geoReference: location.geoReference || {
      latitude: '',
      longitude: ''
    },
    city: location.city,
    state: location.state,
    customer: location.customer || '',
    turbines: location.turbines?.map((t: any) => ({ id: t.id })) || []
  }));

const filterLocationsBySearch = (
  locations: LocationData[],
  searchTerm: string
): LocationData[] => {
  if (!searchTerm.trim()) return locations;
  const term = searchTerm.toLowerCase();
  return locations.filter(
    loc =>
      (loc.name || '').toLowerCase().includes(term) ||
      (loc.customer || '').toLowerCase().includes(term)
  );
};

const getLocationsTableColumns = (
  showActions: boolean,
  onUnassignClick?: (row: LocationData) => void,
  onRowClick?: (row: LocationData) => void
): DataTableColumn<LocationData>[] => {
  const baseColumns: DataTableColumn<LocationData>[] = [
    {
      field: 'name',
      headerName: 'NAME',
      width: showActions ? 'auto' : 'auto',
      sortable: true,
      alignCenter: true,
      renderCell: ({ row }) =>
        onRowClick ? (
          <button
            onClick={() => onRowClick(row)}
            className={styles.rowNameLink}
          >
            {row.name}
          </button>
        ) : (
          <div className={styles.locationNameContainer}>{row.name}</div>
        )
    },
    {
      field: 'turbines',
      headerName: '# OF TURBINES',
      width: 'auto',
      sortable: true,
      type: 'number',
      alignCenter: true,
      renderCell: ({ row }) => (row.turbines ? row.turbines.length : 0)
    },
    {
      field: 'status',
      headerName: 'STATUS',
      width: 'auto',
      sortable: true,
      alignCenter: true,
      renderCell: ({ row }) => <StatusIndicator location={row} />
    }
  ];
  if (showActions && onUnassignClick) {
    baseColumns.push({
      headerName: 'ACTIONS',
      width: 'auto',
      renderCell: ({ row }) => (
        <Button
          label="UNASSIGN"
          icon={UnlinkIcon}
          iconPosition="right"
          onClick={() => onUnassignClick(row)}
          color="blue"
          solid={false}
          size="small"
          className={styles.unassignButton}
        />
      )
    });
  }
  return baseColumns;
};

const getSearchLocationsColumns = (): DataTableColumn<LocationData>[] => [
  {
    field: 'name',
    headerName: 'NAME',
    width: '50%',
    sortable: true,
    renderCell: ({ row }) => (
      <div className={styles.locationNameContainer}>{row.name}</div>
    )
  },
  {
    field: 'status',
    headerName: 'STATUS',
    width: '50%',
    sortable: true,
    renderCell: ({ row }) => <StatusIndicator location={row} />
  }
];

export type OnAssignLocations = (
  locations: Array<{ id: string; name: string }>,
  options?: { onSuccess?: () => void }
) => Promise<void>;

export type OnUnassignLocation = (locationId: string) => Promise<void>;

export const LocationsAssignedTable = ({
  locationsForTable,
  canAssignUnassignLocations = false,
  onAssignLocations,
  onUnassignLocation,
  onRowClick,
  loading
}: LocationsAssignedTableProps) => {
  const { updateModalContent, closeModal } = useModal();
  const [isLocationModalOpen, setIsLocationModalOpen] = useState(false);
  const [locationsSearchTerm, setLocationsSearchTerm] = useState('');
  const [selectedLocations, setSelectedLocations] = useState<
    Array<{ id: string; name: string }>
  >([]);

  const {
    data: dataLocations,
    isLoading: isLoadingLocations,
    error: errorLocations,
    isError: isErrorLocations
  } = useGetLocationsList(
    { pageSize: 100, method: 'firstPage' },
    {
      useQueryOptions: {
        enabled: isLocationModalOpen,
        staleTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false
      }
    }
  );

  const rawLocations = dataLocations?.data || [];
  const locationsForModal = useMemo(() => {
    const transformed = getLocationsRows(rawLocations);
    return filterLocationsBySearch(transformed, locationsSearchTerm);
  }, [rawLocations, locationsSearchTerm]);

  const handleCloseLocationModal = useCallback(() => {
    closeModal();
    setIsLocationModalOpen(false);
    setLocationsSearchTerm('');
    setSelectedLocations([]);
  }, [closeModal]);

  const handleLocationsSearchChange = useCallback((value: string) => {
    setLocationsSearchTerm(value);
  }, []);

  const handleOpenAssignModal = useCallback(() => {
    setLocationsSearchTerm('');
    setSelectedLocations([]);
    setIsLocationModalOpen(true);
  }, []);

  const updateLocationModalContent = useCallback(() => {
    if (!isLocationModalOpen) return;

    const selectedAsLocationData = selectedLocations.map(s => ({
      id: s.id,
      name: s.name,
      type: '',
      category: '',
      serialNumber: '',
      geoReference: { latitude: '', longitude: '' },
      customer: '',
      turbines: []
    })) as LocationData[];

    const createModalContent = () => {
      if (isErrorLocations) {
        return (
          <div className={styles.errorContainer}>
            Error loading locations:{' '}
            {errorLocations?.message || 'Unknown error'}
          </div>
        );
      }
      if (isLoadingLocations) {
        return (
          <div className={styles.formModal}>
            <SearchComponent
              icon={PowerIcon}
              title="Select a Location"
              onSearchChange={handleLocationsSearchChange}
            />
            <div className={styles.formBody}>
              <div className={styles.loadingContainer}>
                <Loader size="large" />
              </div>
            </div>
          </div>
        );
      }
      return (
        <div className={styles.formModal}>
          <SearchComponent
            icon={PowerIcon}
            title="Select a Location"
            onSearchChange={handleLocationsSearchChange}
          />
          <div className={styles.formBody}>
            <DataTable
              rows={locationsForModal}
              className={styles.dataTableModal}
              columns={getSearchLocationsColumns()}
              selectable={true}
              selectedRows={selectedAsLocationData as any}
              onRowSelectionChange={(rows: LocationData[]) =>
                setSelectedLocations(
                  rows.map(r => ({ id: r.id, name: r.name || '' }))
                )
              }
              getRowId={row => row.id}
              initialState={{
                sorting: { field: 'name', sort: 'asc' as const }
              }}
            />
          </div>
        </div>
      );
    };

    const buttonLabel =
      selectedLocations.length > 0
        ? `ASSIGN ${selectedLocations.length} LOCATION${selectedLocations.length > 1 ? 'S' : ''}`
        : 'ASSIGN LOCATION';

    updateModalContent(createModalContent(), {
      modalButtons: [
        {
          label: 'CANCEL',
          onClick: handleCloseLocationModal,
          variant: 'cancelBlue'
        },
        {
          label: buttonLabel,
          onClick: async () => {
            if (selectedLocations.length > 0 && onAssignLocations) {
              try {
                await onAssignLocations(selectedLocations, {
                  onSuccess: handleCloseLocationModal
                });
              } catch (error) {
                console.error('Error in onAssignLocations:', error);
              }
            }
          },
          variant: 'okBlue',
          disabled: selectedLocations.length === 0,
          isLoader: true
        }
      ]
    });
  }, [
    isLocationModalOpen,
    isErrorLocations,
    errorLocations,
    isLoadingLocations,
    locationsForModal,
    selectedLocations,
    handleCloseLocationModal,
    handleLocationsSearchChange,
    onAssignLocations,
    updateModalContent
  ]);

  useEffect(() => {
    if (isLocationModalOpen) {
      updateLocationModalContent();
    }
  }, [isLocationModalOpen, updateLocationModalContent]);

  const handleUnassignClick = useCallback(
    (location: LocationData) => {
      if (!onUnassignLocation) return;
      updateModalContent(null, {
        modalTitle: 'Unassign Location',
        modalIcon: PowerIcon,
        className: styles.unassignModalContainer,
        modalButtons: [
          {
            label: 'CANCEL',
            onClick: () => closeModal(),
            variant: 'cancelBlue'
          },
          {
            label: 'UNASSIGN LOCATION',
            onClick: async () => {
              try {
                await onUnassignLocation(location.id);
              } catch (error) {
                console.error('Error in unassign location:', error);
              }
            },
            variant: 'okBlue',
            isLoader: true
          }
        ],
        alignButtons: 'center',
        contentTitle: 'You are about to unassign',
        contentSubtitle: `Location ${location.name}`,
        contentDescription: (
          <div className={styles.contentDescription}>
            <span>Are you sure you want to proceed?</span>
          </div>
        )
      });
    },
    [updateModalContent, closeModal, onUnassignLocation]
  );

  const columns = useMemo(
    () =>
      getLocationsTableColumns(
        canAssignUnassignLocations,
        canAssignUnassignLocations ? handleUnassignClick : undefined,
        onRowClick
      ),
    [canAssignUnassignLocations, handleUnassignClick, onRowClick]
  );

  return (
    <div className={styles.adminContainer}>
      <AdminCardHeader
        left={
          <>
            <PowerIcon className={styles.adminIconHeader} />
            <h2 className={styles.adminTitle}>Locations</h2>
          </>
        }
        right={
          canAssignUnassignLocations && onAssignLocations ? (
            <PlusButton
              onClick={handleOpenAssignModal}
              label="Assign a location"
            />
          ) : undefined
        }
      />
      {locationsForTable.length > 0 ? (
        <AdminTableBody>
          <div className={styles.adminTableContainer}>
            <DataTable
              rows={locationsForTable}
              columns={columns}
              initialState={{
                sorting: { field: 'name', sort: 'asc' }
              }}
            />
          </div>
        </AdminTableBody>
      ) : (
        <AdminCardBody>
          <div className={styles.adminContainerCenteredRow}>
            {loading ? (
              <ContainerLoader />
            ) : (
              <h4 className={styles.adminText}>
                No locations have been assigned yet
              </h4>
            )}
          </div>
        </AdminCardBody>
      )}
    </div>
  );
};
